import React, { useEffect } from 'react';

import { CloudProviders } from '@ariksa/cloud-account/api';
import { Box, Flex, HStack, Stack, useDisclosure } from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Card, PageHeaderWithIcon } from 'components/DataDisplay';
import { LearnMore } from 'components/DataDisplay/KnowledgeBase/Components/LearnMore';
import { useKnowledgeBase } from 'components/DataDisplay/KnowledgeBase/hook/useKnowledgeBase';
import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { AddButton, OutlinedIconButton } from 'components/DataEntry';
import { LearnMoreButton } from 'components/DataEntry/Button/LearnMoreButton';
import { MaskIcon, PolicyScrollIcon } from 'components/Icons';
import { selectApp } from 'containers/App/selectors';
import { DataAccessGettingStarted } from 'containers/Jit/Components/DataAccessGettingStarted';
import { DataPoliciesTable } from 'containers/Jit/MaskingPolicies/Components/DataPoliciesTable';
import { EnableZeroTrustAccessModal } from 'containers/Jit/MaskingPolicies/Components/EnableZeroTrustAccess/EnableZeroTrustAccessModal';

import { seletMaskingPolicies } from '../selectors';
import { actions } from '../slice';

interface Props {
  basePath?: string;
}

export const DataPolicyComponent = ({
  basePath = '/jit/data/policy',
}: Props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setLearnMore } = useKnowledgeBase();
  const { cloudAccounts } = useSelector(selectApp);
  const { dataPolicies } = useSelector(seletMaskingPolicies);
  const [hasStarted, setHasStarted] = React.useState(false);
  const [hasCheckedDataPolicies, setHasCheckedDataPolicies] = React.useState(
    false,
  );
  const zeroTrustDisclosure = useDisclosure();
  const snowflakeAccounts = cloudAccounts.data?.filter(
    account =>
      account.cloud_type === CloudProviders.Snowflake &&
      account.data_access_mgmt_status === 'SUCCESS',
  );

  useEffect(() => {
    dispatch(
      actions.getDataPolicies({
        q: {
          page: dataPolicies.page.info.page_number,
          size: dataPolicies.page.info.page_size,
        },
      }),
    );
  }, [
    dataPolicies.page.info.page_number,
    dataPolicies.page.info.page_size,
    dispatch,
  ]);

  useEffect(() => {
    if (dataPolicies.isSuccess) setHasCheckedDataPolicies(true);
  }, [dataPolicies.isSuccess]);

  const showGettingStarted =
    dataPolicies.isSuccess && isEmpty(dataPolicies.data) && !hasStarted;

  if (
    cloudAccounts.loading ||
    (!hasCheckedDataPolicies && dataPolicies.isLoading)
  ) {
    return (
      <Card>
        <CustomSpinner size="xl" />
      </Card>
    );
  }

  if (showGettingStarted) {
    return (
      <Card styles={{ cardContent: { p: 0 } }}>
        <DataAccessGettingStarted onGetStarted={() => setHasStarted(true)} />
      </Card>
    );
  }

  return (
    <Card>
      <Stack w="full" spacing={5}>
        <HStack justify="space-between" w="full">
          <PageHeaderWithIcon
            label="Masking Policies"
            icon={<PolicyScrollIcon />}
            iconStyles={{ p: 1 }}
          />
          <Flex justify="flex-end">
            <HStack>
              <LearnMoreButton
                onOpen={() => setLearnMore('data_policy')}
                label="Data Policy"
              />
              {dataPolicies.isSuccess && isEmpty(dataPolicies.data) && (
                <OutlinedIconButton
                  leftIcon={<MaskIcon />}
                  onClick={zeroTrustDisclosure.onOpen}
                  isDisabled={
                    cloudAccounts.loading || !snowflakeAccounts?.length
                  }
                >
                  Enable Zero Trust Access
                </OutlinedIconButton>
              )}
              <AddButton
                label="Add Policy"
                onClick={() => navigate(`${basePath}/add`)}
              />
            </HStack>
          </Flex>
        </HStack>
        <Box flex={1}>
          <DataPoliciesTable />
        </Box>
      </Stack>
      <LearnMore />
      {zeroTrustDisclosure.isOpen && (
        <EnableZeroTrustAccessModal
          isOpen={zeroTrustDisclosure.isOpen}
          onClose={zeroTrustDisclosure.onClose}
          accounts={snowflakeAccounts ?? []}
        />
      )}
    </Card>
  );
};
